<template>
  <d-container
    fluid
    class="main-content-container px-4">
    <d-checkbox
      v-if="userIsWorker"
      inline
      v-model="showAll"
      :disabled="!loaded && !loadError"
      toggle
      toggle-small
      class="is-moved-to-top">
      {{ $t('order.showAllOrders') }}
    </d-checkbox>

    <d-row
      style="width: 100%; gap: 0.75rem;"
      class="d-flex ml-0 mb-3 align-items-center justify-content-end">
      <d-button
        v-if="dashboardParam"
        theme="primary"
        @click.prevent="$bus.$emit('dashboard-contractors-settlements-import-popup-open')">
        <i class="material-icons">cloud_upload</i>
        {{ $t('contractorsSettlementsImportPopup.importXlsx') }}
      </d-button>

      <d-button
        v-if="dashboardParam"
        theme="success"
        :disabled="downloadInProgress"
        @click.prevent.stop="loadFilteredData(true)">
        <div
          v-if="downloadInProgress"
          class="spinner-border spinner-border-sm"
          role="status"></div>
        <template v-if="!downloadInProgress">
          <i class="material-icons">cloud_download</i>
          {{ $t('order.downloadXls') }}
        </template>
      </d-button>
    </d-row>

    <list-filters
      class="mb-3"
      :config="filtersConfig"
      :isListLoaded="allDataLoaded"
      :view-name="'contractors-settlements'"
      @init="handleFiltersInit"
      @search="handleFiltersSearch" />

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th
              scope="col"
              class="border-0">{{ $t('contractor.contractor') }}</th>
            <th
              scope="col"
              class="border-0"
              style="width: 180px;">{{ $t('service.serviceName') }}</th>
            <th
              scope="col"
              class="border-0"
              style="width: 160px;">{{ $t('service.serviceGroup') }}</th>
            <th
              scope="col"
              class="border-0"
              style="width: 150px;">{{ $t('contractor.unitPrice') }}</th>
            <th
              scope="col"
              class="border-0"
              style="width: 100px;">{{ $t('ui.pieces') }}</th>
            <th
              scope="col"
              class="border-0"
              style="width: 180px;">{{ $t('ui.order') }}</th>
            <th
              scope="col"
              class="border-0"
              style="width: 300px;">{{ $t('contractor.dateOfSampleAndAcceptance') }}</th>
            <th
              scope="col"
              class="border-0"
              style="width: 160px;">{{ $t('contractor.serviceCost') }}</th>
            <th
              scope="col"
              class="border-0"
              style="width: 120px">{{ $t('settlements.settlement') }}</th>
          </tr>
        </thead>
        <tbody v-if="!loadError">
          <tr
            v-for="(settlement, index) of contractorsSettlements"
            :key="'settlement-' + index">
            <td class="align-middle">
              {{ settlement.contractor_name }}
            </td>
            <td class="align-middle">
              {{ settlement.name }}
            </td>
            <td class="align-middle">
              {{ settlement.group_name }}
            </td>
            <td class="align-middle">
              {{ Number(settlement.contractor_unit_price).toFixed(2) }} {{ settlement.currency_code }}
            </td>
            <td class="align-middle">
              {{ settlement.quantity }} {{ $t('ui.piecesAbbr') }}
            </td>
            <td class="align-middle">
              {{ settlement.order_name }}
            </td>
            <td class="align-middle">
              <small><strong>{{ $t('service.sample.sampleDate') }}:</strong> {{ settlement.obtaining_at }}</small><br>
              <small><strong>{{ $t('contractor.acceptanceDate') }}:</strong> {{ settlement.accepted_at }}</small><br>
            </td>
            <td class="align-middle">
              {{ Number(settlement.contractor_price).toFixed(2) }} {{ settlement.currency_code }}
            </td>
            <td class="align-middle">
              <d-button
                v-if="settlement.can_settlement"
                theme="primary"
                @click.native="makeSettlement(settlement.id)"
                :disabled="settlementInProgress === settlement.id"
                class="mr-2"
                :title="$t('settlements.settle')">
                <template v-if="settlementInProgress !== settlement.id">{{ $t('settlements.settle') }}</template>
                <template v-else>
                  <div
                    class="spinner-border spinner-border-sm"
                    role="status"></div>
                </template>
              </d-button>

              <small
                v-else
                class="text-success text-center">
                <strong>{{ $t('settlements.settled') }}:</strong><br>
                {{ settlement.settlement_at }}
              </small>
            </td>
          </tr>
          <tr v-if="contractorsSettlements.length === 0">
            <td
              colspan="8"
              class="text-center">
              {{ $t('contractor.noItemsToBeSettled') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-if="!allDataLoaded"
        class="pb-3 pt-3 text-center overlay-loading">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a
          href="javascript:window.location.reload();"
        class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import FormUtils from '@/utils/FormUtils.js';
import HasPagination from '@/mixins/HasPagination.vue';
import ListUtils from '@/utils/ListUtils.js';
import Pagination from '@/components/common/Pagination.vue';

export default {
  name: 'contractors-settlements',
  mixins: [
    HasPagination
  ],
  components: {
    'pagination': Pagination
  },
  props: {
    dashboardParam: {
      default: false,
      type: Boolean
    },
    popupShowAll: {
      default: false,
      type: Boolean
    }
  },
  computed: {
    allDataLoaded () {
      return this.loaded && this.loadedContractors;
    },
    userIsWorker () {
      return this.$store.getters.getUserType === 'worker';
    },
    contractorOptions () {
      return this.contractors.map(contractor => ({
        text: contractor.name,
        value: contractor.id
      }));
    },
    filtersConfig () {
      return [
        {
          key: 'order',
          label: this.$t('ui.order'),
          placeholder: this.$t('order.orderNumber'),
          type: 'text',
          isBasic: true,
          defaultValue: ''
        },
        {
          key: 'contractor_id',
          label: this.$t('contractor.contractor'),
          placeholder: this.$t('contractor.chooseContractor'),
          type: 'select',
          isBasic: true,
          defaultValue: null,
          options: this.contractorOptions
        },
        {
          key: 'only_ended',
          label: this.$t('contractor.chooseEndedStatus'),
          placeholder: this.$t('contractor.chooseEndedStatus'),
          type: 'select',
          isBasic: true,
          defaultValue: null,
          options: [
            { text: this.$t('contractor.all'), value: 0 },
            { text: this.$t('contractor.onlyEnded'), value: 1 }
          ]
        },
        {
          key: 'settlement_status',
          label: this.$t('contractor.chooseSettlementStatus'),
          placeholder: this.$t('contractor.chooseSettlementStatus'),
          type: 'select',
          isBasic: true,
          defaultValue: this.dashboardParam ? 'unsettled' : null,
          options: [
            { text: this.$t('contractor.all'), value: '' },
            { text: this.$t('ui.settled'), value: 'settled' },
            { text: this.$t('contractor.toBeSettled'), value: 'unsettled' }
          ]
        }
      ];
    }
  },
  data () {
    return {
      contractorsSettlements: [],
      contractors: [],
      loaded: false,
      loadedContractors: false,
      loadError: false,
      settlementInProgress: false,
      showAll: !!this.popupShowAll,
      downloadInProgress: false,
      activeFilters: {},
      apiQueryPayload: {}
    };
  },
  watch: {
    showAll () {
      this.$bus.$emit('pagination-reset', false);
    }
  },
  mounted () {
    this.loadAdditionalData();
    this.$bus.$on('contractors-settlements-reload-needed', this.loadFilteredData);
  },
  methods: {
    handleFiltersInit (filters, apiPayload) {
      this.activeFilters = filters;
      this.apiQueryPayload = apiPayload;

      this.loadFilteredData();
    },
    handleFiltersSearch (activeFilters, apiPayload) {
      this.activeFilters = activeFilters;
      this.apiQueryPayload = apiPayload;
      this.$bus.$emit('pagination-reset');
      this.debouncedLoadFilteredData();
    },
    debouncedLoadFilteredData: debounce(function () {
      this.loadFilteredData();
    }, 250),
    loadFilteredData (downloadAsFile = false) {
      let whereData = { ...this.apiQueryPayload };
      let showAllParam = (this.userIsWorker && this.showAll) ? 1 : 0;

      if (downloadAsFile) {
        if (this.downloadInProgress) {
          return;
        }

        whereData['download_excel'] = 1;
        this.downloadInProgress = true;

        this.$http.post('/orders/services/settlements', {
          where: whereData,
          show_all: showAllParam
        }, {
          responseType: 'blob'
        }).then((response) => {
          let link = document.createElement('a');
          link.href = URL.createObjectURL(new Blob([response.data]));
          link.setAttribute('download', this.$t('order.ordersXlsFilename') + '.xlsx');
          document.querySelector('.big-popup .modal-body').appendChild(link);
          link.click();
          document.querySelector('.big-popup .modal-body').removeChild(link);
          this.downloadInProgress = false;
        }).catch(() => {
          this.downloadInProgress = false;
        });
      } else {
        ListUtils.loadPaginatedItemsData(this, {
          endpoint: '/orders/services/settlements?page=' + this.currentPage,
          listField: 'contractorsSettlements',
          loadError: 'loadError',
          method: 'get',
          params: {
            where: whereData,
            show_all: showAllParam
          }
        });
      }
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/contractors/items',
        loadedKey: 'loadedContractors',
        outputKey: 'contractors',
        errorKey: 'loadError',
        params: {
          return: 'get'
        }
      });
    },
    makeSettlement (settlementID) {
      this.settlementInProgress = settlementID;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/orders/services/settlement/' + settlementID,
        method: 'get',
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('contractor.makeSettlementErrorText'),
        successAction: () => {
          this.settlementInProgress = false;
          this.debouncedLoadFilteredData();
        },
        failAction: () => {
          this.settlementInProgress = false;
        }
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('contractors-settlements-reload-needed', this.loadFilteredData);
  }
};
</script>

<style lang="scss" scoped>
.is-moved-to-top {
  left: auto;
  position: absolute;
  right: 60px;
  top: -43px;
}
</style>
